/**
 * Hook for managing the NWC (Nostr Wallet Connect) client lifecycle.
 *
 * Creates, connects, and manages an NWCClient instance from @getalby/sdk.
 * Handles WebSocket lifecycle, status tracking, auto-reconnect with
 * exponential backoff, and cleanup on unmount to prevent leaks.
 *
 * Security: The NWC URI is never stored in state or logged. It flows
 * from the connect() call -> NWCClient constructor -> ref. That's it.
 *
 * Tier: 2 (Lightning features).
 *
 * @module lightning/hooks/useNwcClient
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { NWCClient } from '@getalby/sdk';

/** Connection status for the NWC client. */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/** Return type for the useNwcClient hook. */
export interface UseNwcClientResult {
  /** The NWCClient instance, or null if not connected. */
  readonly client: NWCClient | null;
  /** Current connection status. */
  readonly status: ConnectionStatus;
  /** Error message if status is 'error', null otherwise. */
  readonly error: string | null;
  /** Connect to a wallet using an NWC URI. */
  readonly connect: (uri: string) => Promise<void>;
  /** Disconnect the current client. */
  readonly disconnect: () => void;
  /** Reconnect using the last URI. */
  readonly reconnect: () => Promise<void>;
}

/** Backoff delays in ms: 1s, 2s, 4s, 8s, 16s, 30s cap. */
const BACKOFF_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

/** Maximum total reconnect time in ms (5 minutes). */
const MAX_RECONNECT_TIME_MS = 300_000;

/**
 * Get the backoff delay for a given attempt number.
 *
 * @param attempt - Zero-based attempt number
 * @returns Delay in milliseconds
 */
export function getBackoffDelay(attempt: number): number {
  const index = Math.min(attempt, BACKOFF_DELAYS_MS.length - 1);
  return BACKOFF_DELAYS_MS[index] ?? 30_000;
}

/**
 * Manage the full NWC client lifecycle.
 *
 * Creates an NWCClient from a wallet connect URI, verifies the connection
 * with getInfo(), tracks status, and auto-reconnects with exponential backoff
 * on disconnection. Cleans up the client on unmount.
 *
 * @returns Object with client, status, error, connect, disconnect, reconnect
 */
export function useNwcClient(): UseNwcClientResult {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);

  const clientRef = useRef<NWCClient | null>(null);
  const uriRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectStartRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);

  // Clear any pending reconnect timer
  const clearReconnectTimer = useCallback((): void => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const disconnect = useCallback((): void => {
    clearReconnectTimer();
    reconnectStartRef.current = null;
    reconnectAttemptRef.current = 0;

    if (clientRef.current) {
      clientRef.current.close();
      clientRef.current = null;
    }

    if (mountedRef.current) {
      setStatus('disconnected');
      setError(null);
    }
  }, [clearReconnectTimer]);

  const connect = useCallback(
    async (uri: string): Promise<void> => {
      // Clean up any existing connection
      clearReconnectTimer();
      if (clientRef.current) {
        clientRef.current.close();
        clientRef.current = null;
      }

      uriRef.current = uri;
      reconnectStartRef.current = null;
      reconnectAttemptRef.current = 0;

      if (mountedRef.current) {
        setStatus('connecting');
        setError(null);
      }

      try {
        const client = new NWCClient({ nostrWalletConnectUrl: uri });
        if (!mountedRef.current) {
          client.close();
          return;
        }

        // Verify connection works by calling getInfo
        await client.getInfo();

        if (!mountedRef.current) {
          client.close();
          return;
        }

        clientRef.current = client;
        setStatus('connected');
        setError(null);
      } catch (err: unknown) {
        if (!mountedRef.current) return;

        const message = err instanceof Error ? err.message : 'Connection failed';
        setStatus('error');
        setError(message);
      }
    },
    [clearReconnectTimer],
  );

  const reconnect = useCallback(async (): Promise<void> => {
    const uri = uriRef.current;
    if (!uri) return;

    // Initialize reconnect tracking if this is the first attempt
    if (reconnectStartRef.current === null) {
      reconnectStartRef.current = Date.now();
    }

    // Check if we've exceeded the maximum reconnect time
    const elapsed = Date.now() - reconnectStartRef.current;
    if (elapsed >= MAX_RECONNECT_TIME_MS) {
      if (mountedRef.current) {
        setStatus('error');
        setError('Reconnect timeout exceeded');
      }
      return;
    }

    // Save reconnect state before connect() (which resets it for user-initiated connects)
    const savedStart = reconnectStartRef.current;
    const savedAttempt = reconnectAttemptRef.current;

    await connect(uri);

    // Restore reconnect tracking (connect() resets these for fresh connections)
    reconnectStartRef.current = savedStart;
    reconnectAttemptRef.current = savedAttempt;

    // If connect failed and we're still mounted, schedule another attempt
    if (mountedRef.current && clientRef.current === null && uriRef.current) {
      const delay = getBackoffDelay(reconnectAttemptRef.current);
      reconnectAttemptRef.current += 1;

      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current && uriRef.current) {
          void reconnect();
        }
      }, delay);
    }
  }, [connect]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      clearReconnectTimer();
      if (clientRef.current) {
        clientRef.current.close();
        clientRef.current = null;
      }
    };
  }, [clearReconnectTimer]);

  return {
    client: clientRef.current,
    status,
    error,
    connect,
    disconnect,
    reconnect,
  };
}
